import { c as _c } from "react/compiler-runtime";
import { House } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useChatUnreadTotal } from "@/api/use-chats";
import { ChatFilledIcon } from "@/components/chat/chat-filled-icon";
import { BagIcon, CategoryIcon, ChatIcon, HeartIcon, HomeBoldIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { useCart } from "@/store";

// Нижний таб-бар планшета/мобильного (< 1024px) — кадр «Tablet» 744, «Планшет.svg»:
//   • полоса белая, сверху обводка 1px #E2D7D7 12 %, внутренние отступы 10/16/0/16;
//   • «Menu Bar» — пилюля 56px с отступом 6, пять равных пунктов 44px (r22);
//   • активный пункт — подложка #000 5 % (rx 22), у «Главной» иконка залитая;
//   • иконки 24px #2E2E2E — векторы макета (DS): Outline/Category, Outline/Heart,
//     bag New, Outline/Chat; активная «Главная» — Bold/Home, активный «Чат» —
//     залитый пузырь (A-04, как в шапке). Контурной «Главной» в макете нет (на
//     всех кадрах она активна) — для неактивной остаётся контур lucide 1,5;
//     подписей в макете не видно (только aria-label);
//   • ниже — зона home indicator: в вебе это env(safe-area-inset-bottom).
// Счётчики — «indicator» из макета (15×15, r1000, #FA5C2E, 8px bold): корзина и
// непрочитанные чаты (тот же хук, что у иконки чата в шапке).
// Высота без safe-area: 1 + 10 + 56 = 67px — на неё SiteLayout даёт нижний отступ.

type Tab = {
  to: string;
  label: string;
  icon: (active: boolean) => React.ReactNode;
  isActive: (pathname: string) => boolean;
  count?: number;
};
export function MobileTabBar() {
  const $ = _c(15);
  const {
    t
  } = useTranslation();
  const {
    pathname
  } = useLocation();
  const cartCount = useCart(_temp);
  const chatCount = useChatUnreadTotal();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== cartCount || $[1] !== chatCount || $[2] !== pathname || $[3] !== t) {
    const tabs = [{
      to: "/",
      label: t("nav.feed"),
      icon: _temp2,
      isActive: _temp3
    }, {
      to: "/catalog",
      label: t("nav.catalog"),
      icon: _temp4,
      isActive: _temp5
    }, {
      to: "/wishlist",
      label: t("nav.wishlist"),
      icon: _temp6,
      isActive: _temp7
    }, {
      to: "/cart",
      label: t("nav.cart"),
      icon: _temp8,
      isActive: _temp9,
      count: cartCount
    }, {
      to: "/chats",
      label: "\u0427\u0430\u0442\u044B",
      icon: _temp0,
      isActive: _temp1,
      count: chatCount
    }];
    t2 = "\u041D\u0430\u0432\u0438\u0433\u0430\u0446\u0438\u044F";
    t3 = "fixed inset-x-0 bottom-0 z-40 border-t border-[#E2D7D7]/12 bg-white px-4 pt-2.5 pb-[env(safe-area-inset-bottom)] lg:hidden";
    t0 = "mx-auto flex p-1.5";
    t1 = tabs.map(t4 => {
      const {
        to,
        label,
        icon,
        isActive,
        count: t5
      } = t4;
      const count = t5 === undefined ? 0 : t5;
      const active = isActive(pathname);
      return <Link key={to} to={to} aria-label={count > 0 ? `${label} (${count})` : label} aria-current={active ? "page" : undefined} className={cn("flex h-11 flex-1 items-center justify-center rounded-[22px] text-osmix-text-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40", active ? "bg-black/5" : "hover:bg-black/[0.03]")}><span className="relative flex size-6 items-center justify-center">{icon(active)}{count > 0 && <span className="absolute -top-1 -right-1.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-osmix-tangelo px-0.5 text-[8px] font-bold leading-[10px] text-white">{count > 99 ? "99+" : count}</span>}</span></Link>;
    });
    $[0] = cartCount;
    $[1] = chatCount;
    $[2] = pathname;
    $[3] = t;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t0 || $[9] !== t1) {
    t4 = <div className={t0}>{t1}</div>;
    $[8] = t0;
    $[9] = t1;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <nav aria-label={t2} className={t3}>{t4}</nav>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp1(p_3) {
  return p_3.startsWith("/chats");
}
function _temp0(a_0) {
  return a_0 ? <ChatFilledIcon size={24} /> : <ChatIcon size={24} />;
}
function _temp9(p_2) {
  return p_2.startsWith("/cart");
}
function _temp8() {
  return <BagIcon size={24} />;
}
function _temp7(p_1) {
  return p_1.startsWith("/wishlist");
}
function _temp6() {
  return <HeartIcon size={24} />;
}
function _temp5(p_0) {
  return p_0.startsWith("/catalog");
}
function _temp4() {
  return <CategoryIcon size={24} />;
}
function _temp3(p) {
  return p === "/";
}
function _temp2(a) {
  return a ? <HomeBoldIcon size={24} /> : <House className="size-6" strokeWidth={1.5} aria-hidden="true" />;
}
function _temp(s) {
  return s.cart?.itemCount ?? 0;
}